"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoButton, NeoDatePicker, NeoTextBox} from "@/components/forms";
import {UploadFormField} from "@/components/pages/expensereports/uploadform";
import {NeoHeader, PlusIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {FleetService} from "@/service/fleet.service";
import {yupResolver} from "@hookform/resolvers/yup";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import Select from "react-select";
import * as yup from "yup";

export type FormMaintance = {
  vehicleId: string;
  id?: string;
  date?: Date | null;
  totalAmount: number;
  expenseType: number;
  vendorId?: number | null;
  description?: string | null;
};

type MaintanceFormProps = {
  data: any;
  onSubmit: (formValues: any) => void | boolean | Promise<boolean | void>;
  onClose: any;
};

export const ExpenseMaintanceForm: React.FC<MaintanceFormProps> = ({
  data,
  onSubmit,
  onClose
}) => {
  const maxDate = useMemo(() => new Date(), []);
  const {filterContext} = useNeoContext();
  const [loading, setLoading] = useState(false);
  const [loginUserId, setLoginUserId] = useState("");
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [servicetypes, setServiceTypes] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [defaultformdata, setdefaultformdata] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalName, setModalName] = useState("");
  const [modalData, setModalData] = useState(null);
  const [expenseSettingType, setExpenseSettingType] = useState(null);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const toNumOrUndef = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    if (typeof v === "string") {
      const s = v.trim().replace(/,/g, "");
      if (!s) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  const schema = yup.object().shape({
    vehicleId: yup.string().required("Please select vehicle"),
    id: yup.string().optional(),
    date: yup
      .date()
      .transform((v, o) => {
        if (o === "" || o == null) return null;
        const d = new Date(o);
        return isNaN(d.getTime()) ? null : d;
      })
      .nullable()
      .typeError("Enter a valid date")
      .max(today, "Date cannot be in the future"),
    expenseType: yup.number().nullable().required("Please select service type"),
    totalAmount: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Total cost must be a number")
      .moreThan(0, "Total cost must be greater than 0")
      .max(100000, "Total cost must be less than or equal to 1,00,000")
      .required("Please enter total cost"),
    vendorId: yup.number().nullable(),
    description: yup.string().nullable().default("")
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: {isSubmitting, errors}
  } = useForm<FormMaintance>({
    mode: "onChange",
    resolver: yupResolver<FormMaintance>(schema),
    shouldUnregister: true
  });

  const mapApiToForm = (apiData: any): FormMaintance => {
    return {
      vehicleId: apiData?.vehicleId ?? "",
      date: apiData?.date,
      expenseType: apiData?.expenseType ?? "",
      totalAmount: toNumOrUndef(apiData?.totalAmount) ?? 0,
      vendorId: apiData?.vendorId ?? "",
      description: apiData?.description ?? ""
    };
  };

  const handleApiResponse = (apiResponse: any) => {
    const formValues = mapApiToForm(apiResponse);
    reset(formValues);
  };

  useEffect(() => {
    setValue("date", maxDate);
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext?.client?.institutionUserId]);

  const fetchVehicleDatas = useCallback(async () => {
    try {
      const requestData = {
        filters: {
          branch: [loginUserId]
        }
      };
      const response = await ExpenseService.getvehicles(requestData);
      if (
        response?.data?.status === "success" &&
        response?.data?.data?.reportData
      ) {
        setVehicles(response.data.data.reportData);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const fetchfleetdatas = useCallback(async () => {
    try {
      const response = await FleetService.getfleetdatas(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        const list = response.data.data?.["repairExpenseTypes"] ?? [];
        const updatedlist = [
          {id: "", name: "Please select Service Types "},
          ...list
        ];
        setServiceTypes(updatedlist);
        const vendorlist = response.data.data?.["vendors"] ?? [];
        const updatedvendorList = [
          {id: "", name: "Please select Vendor/Workshop"},
          ...vendorlist
        ];
        setVendors(updatedvendorList);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const getmaintancedata = useCallback(async () => {
    const requestdata = {
      expenseType: 2,
      expenseId: data?.id
    };
    try {
      const response = await ExpenseService.getexpenseview(
        loginUserId,
        requestdata
      );
      if (response?.data?.status === "success" && response?.data?.data) {
        const responsedata = response?.data?.data;
        const responsemaintancedata = responsedata.maintenanceExpense;
        setAttachments(
          Array.isArray(responsemaintancedata?.documents)
            ? responsemaintancedata.documents
            : []
        );
        const maintancedata: any = {
          vehicleId: responsemaintancedata?.vehicleId,
          date: responsemaintancedata?.date,
          totalAmount: responsemaintancedata?.totalAmount,
          description: responsemaintancedata?.description,
          vendorId: responsemaintancedata?.vendorId,
          expenseType: responsemaintancedata?.serviceId,
          document: responsemaintancedata.document,
          id: data?.id
        };
        handleApiResponse(maintancedata);
        setdefaultformdata(maintancedata);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId, data?.id]);

  useEffect(() => {
    if (!loginUserId) return;
    (async () => {
      try {
        await fetchVehicleDatas();
        await fetchfleetdatas();
        if (data?.id) {
          await getmaintancedata();
        }
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    })();
  }, [loginUserId, fetchVehicleDatas, fetchfleetdatas]);

  const onDateChange = (name: any, event: any) => {
    clearErrors(name);
    setValue(name, event);
  };

  const onFormSubmit = useCallback(
    async (form: FormMaintance) => {
      const payload = {
        ...form,
        id: data?.id ? String(data.id) : "",
        attachments
      };
      try {
        const result = onSubmit(payload);
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    },
    [attachments, onSubmit]
  );

  const onCancel = useCallback(() => {
    setAttachments(
      Array.isArray(defaultformdata?.documents) ? defaultformdata.documents : []
    );
    if (data?.id) {
      getmaintancedata();
    } else {
      handleApiResponse(defaultformdata);
      setValue("date", maxDate);
    }
  }, [reset, defaultformdata?.documents]);

  const openPopup = (type: any) => {
    setExpenseSettingType(type);
    setModalName("fleetform");
    setModalOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    const requestData: Record<string, any> = {
      type: expenseSettingType,
      name: data.name,
      phone: data.phone,
      address: data.address
    };

    try {
      const response = await FleetService.addfleetdata(
        requestData,
        loginUserId
      );
      if (response?.data?.status === "success") {
        setModalOpen(false);
        await fetchfleetdatas();
        toast.success(response?.data?.message);
      } else {
        toast.error(response?.data?.message || "Something went wrong.");
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  };
  return (
    <>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
        <div className="mb-5 flex-row font-bold">
          <NeoHeader message="Maintenance Expense" />
        </div>

        <form
          key={data?.id ?? "new"}
          onSubmit={handleSubmit(onFormSubmit)}
          noValidate
        >
          {/* Row 1 */}
          <div className="mb-4 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
            {/* Vehicle */}
            <Controller
              name="vehicleId"
              control={control}
              render={({field, fieldState}) => {
                const selectedOpt =
                  vehicles.find(
                    (v: any) => String(v.id) === String(field.value)
                  ) ?? null;
                return (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="vehicleId"
                      className={`font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                    >
                      Vehicle{" "}
                      <span className="text-[#EF4444]" aria-hidden="true">
                        *
                      </span>
                    </label>
                    <Select
                      inputId="vehicleId"
                      value={selectedOpt}
                      onChange={(opt: any) => {
                        field.onChange(opt ? String(opt.id) : "");
                        clearErrors("vehicleId");
                      }}
                      options={vehicles}
                      getOptionLabel={(e: any) => e.regNo}
                      getOptionValue={(e: any) => String(e.id)}
                      placeholder="Select Vehicle"
                      styles={{
                        control: base => ({
                          ...base,
                          minHeight: "43px",
                          borderWidth: "2px",
                          borderColor: fieldState.invalid
                            ? "#f31260"
                            : "#d4d4d8",
                          borderRadius: "12px",
                          paddingLeft: "12px",
                          paddingRight: "12px",
                          boxShadow: "none",
                          fontSize: "14px",
                          backgroundColor: "white"
                        }),
                        indicatorSeparator: () => ({display: "none"}),
                        menu: base => ({
                          ...base,
                          borderRadius: "10px",
                          zIndex: 50
                        })
                      }}
                    />
                    {fieldState.error && (
                      <p
                        className="text-xs text-[#f31260]"
                        id="vehicleId-error"
                      >
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />

            {/* Date */}
            <Controller
              name="date"
              control={control}
              render={({field: {ref, value, onChange, ...fieldRest}}) => (
                <NeoDatePicker
                  {...fieldRest}
                  label={
                    <>
                      Date{" "}
                      <span className="text-[#EF4444]" aria-hidden="true">
                        *
                      </span>
                    </>
                  }
                  placeholder="Please select from date"
                  errorMessage={errors.date && `${errors.date.message}`}
                  maxDate={maxDate}
                  value={value}
                  handleDateChange={(e: any) => {
                    onChange(e);
                    onDateChange("date", e);
                  }}
                />
              )}
            />
          </div>

          {/* Row 2 */}
          <div className="mb-4 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
            {/* Service Types */}
            <Controller
              name="expenseType"
              control={control}
              render={({field, fieldState}) => {
                const selectedOpt =
                  servicetypes.find(
                    (v: any) => String(v.id) === String(field.value)
                  ) ?? null;
                return (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="expenseType"
                      className={`-mt-1 mb-1 flex items-center justify-between font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                    >
                      <span>
                        Service Types{" "}
                        <span className="text-[#EF4444]" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <div
                        title="Add new service"
                        className="inline-flex cursor-pointer items-center text-xs text-[#3B82F6]"
                        onClick={() => openPopup(3)}
                      >
                        <PlusIcon size={15} />
                        Add Service
                      </div>
                    </label>
                    <Select
                      inputId="expenseType"
                      value={selectedOpt}
                      onChange={(opt: any) => {
                        field.onChange(opt ? String(opt.id) : "");
                        clearErrors("expenseType");
                      }}
                      options={servicetypes}
                      getOptionLabel={(e: any) => e.name}
                      getOptionValue={(e: any) => String(e.id)}
                      placeholder="Select service types"
                      styles={{
                        control: base => ({
                          ...base,
                          minHeight: "43px",
                          borderWidth: "2px",
                          borderColor: fieldState.invalid
                            ? "#f31260"
                            : "#d4d4d8",
                          borderRadius: "12px",
                          paddingLeft: "12px",
                          paddingRight: "12px",
                          boxShadow: "none",
                          fontSize: "14px",
                          backgroundColor: "white"
                        }),
                        indicatorSeparator: () => ({display: "none"}),
                        menu: base => ({
                          ...base,
                          borderRadius: "10px",
                          zIndex: 50
                        })
                      }}
                    />
                    {fieldState.error && (
                      <p
                        className="text-xs text-[#f31260]"
                        id="vehicleId-error"
                      >
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />

            {/* Total Amount */}
            <Controller
              name="totalAmount"
              control={control}
              render={({field}) => (
                <NeoTextBox
                  label={
                    <>
                      Amount{" "}
                      <span className="text-[#EF4444]" aria-hidden="true">
                        *
                      </span>
                    </>
                  }
                  id="totalAmount"
                  placeholder="0.00"
                  color={errors.totalAmount ? "danger" : "default"}
                  isInvalid={!!errors.totalAmount}
                  errorMessage={errors.totalAmount?.message as string}
                  {...field}
                  value={
                    field.value !== undefined && field.value !== null
                      ? String(field.value)
                      : ""
                  }
                />
              )}
            />
          </div>

          {/* Row 3 */}
          <div className="mb-4 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
            {/* Vendors */}
            <Controller
              name="vendorId"
              control={control}
              render={({field, fieldState}) => {
                const valueId =
                  field.value == null || String(field.value) === ""
                    ? null
                    : String(field.value);
                const selectedOption = valueId
                  ? (vendors.find((o: any) => String(o.id) === valueId) ?? null)
                  : null;
                return (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="vendorId"
                      className="-mt-1 mb-1 flex items-center justify-between"
                    >
                      Vendor/Workshop
                      <div
                        title="Add new vendor"
                        className="inline-flex cursor-pointer items-center text-xs text-[#3B82F6]"
                        onClick={() => openPopup(2)}
                      >
                        <PlusIcon size={15} />
                        Add Vendor
                      </div>
                    </label>
                    <Select
                      inputId="vendorId"
                      name={field.name}
                      value={selectedOption}
                      options={vendors}
                      onChange={(opt: any) =>
                        field.onChange(opt ? String(opt.id) : null)
                      }
                      getOptionLabel={(e: any) => e.name}
                      getOptionValue={(e: any) => String(e.id)}
                      placeholder="Select Vendor/Workshop"
                      styles={{
                        control: base => ({
                          ...base,
                          minHeight: "43px",
                          borderWidth: "2px",
                          borderColor: "#d4d4d8",
                          borderRadius: "12px",
                          paddingLeft: "12px",
                          paddingRight: "12px",
                          boxShadow: "none",
                          fontSize: "14px",
                          backgroundColor: "white"
                        }),
                        indicatorSeparator: () => ({display: "none"}),
                        menu: base => ({
                          ...base,
                          borderRadius: "10px",
                          zIndex: 50
                        })
                      }}
                    />
                  </div>
                );
              }}
            />
          </div>

          {/* Row 4 */}
          <div className="mb-4 grid grid-cols-1 gap-3 max-sm:grid-cols-1">
            {/* Note */}
            <label htmlFor="Service Details">Service Details</label>
            <Controller
              name="description"
              control={control}
              render={({field}) => (
                <>
                  <textarea
                    {...field}
                    value={field.value ?? ""}
                    className="w-full rounded-[17px] border-2 p-3 shadow-none hover:border-gray-600 focus:outline-none focus:ring-[#888888]"
                    placeholder="Describe the maintenance work performed"
                    rows={4}
                    id="description"
                    autoComplete="off"
                    {...register("description")}
                  />
                  {typeof errors.description?.message === "string" && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.description.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>

          {/* Attachments */}
          {!data?.id && (
            <>
              <div className="mb-4 grid grid-cols-1">
                <UploadFormField
                  value={attachments}
                  onChangeFile={setAttachments}
                  max={3}
                  onError={msg => console.error(msg)}
                />
              </div>
              <hr />
            </>
          )}
          {/* Actions */}
          <div className="flex items-center justify-end gap-2 p-4">
            <NeoButton type="button" color="white" onClick={onCancel}>
              Reset
            </NeoButton>
            <NeoButton
              type="submit"
              disabled={isSubmitting || loading}
              isLoading={isSubmitting || loading}
              spinner={
                <AiOutlineLoading size="xs" className="animate-spin h-6 w-6" />
              }
              color="black"
            >
              Save
            </NeoButton>
          </div>
        </form>
      </div>
      <NeoModal
        onOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        actionType={modalName}
        hasActions={false}
        data={modalData}
        onModalSubmit={handleFormSubmit}
        actionText={
          Number(expenseSettingType) === 2 ? "Vendor" : "Service Type"
        }
      />
    </>
  );
};
